/**
 * Catalogue types, shared.
 *
 * These lived only in `apps/frontend/src/features/catalog/services/catalog-api.service.ts`,
 * which was survivable while one app consumed the catalogue. Plan 008 adds a
 * second (`apps/admin`), and a second app means either an import across an app
 * boundary or a second copy of every shape -- and a second copy drifts.
 *
 * It is not a hypothetical drift. `specifications` was already declared
 * `Record<string, string | number>` on the frontend while the database, the
 * Zod schema and the service all accept `boolean` too. Any product with a
 * boolean spec -- "Anti-skid: true" is the obvious one for tiles -- had a value
 * the compiler insisted could not exist. That is fixed here rather than copied,
 * because copying it is how the admin app would have inherited a bug nobody
 * wrote twice.
 *
 * These describe the API's WIRE FORMAT, not the database rows: dates are ISO
 * strings, money is paise, and optional means the endpoint may omit it.
 */

/**
 * One page of an admin list endpoint.
 *
 * Shared because it was written twice: once in
 * `apps/backend/src/modules/catalog/catalog.service.ts` as the return of
 * `pageOf`, and again in `apps/admin`'s catalogue service as the shape it
 * unwraps. Two identical declarations of one wire format is the arrangement
 * where a field can be renamed on one side and nothing fails to compile on the
 * other.
 *
 * `page` and `perPage` are what the server ACTUALLY used, which is not
 * necessarily what the caller asked for -- both are clamped server-side.
 */
export interface AdminListPage<T> {
	items: T[];
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

export interface CatalogBrand {
	id: string;
	name: string;
	description?: string;
	website?: string;
	logoUrl?: string;
	isActive: boolean;
	/**
	 * The brand this one was merged into, when it was a duplicate.
	 *
	 * OPTIONAL because the public reads genuinely do not send it: `PublicBrand`
	 * is a `Pick` that excludes this field, and `PUBLIC_BRAND_COLUMNS` is the
	 * projection that enforces it -- plan 009 added the column and the public
	 * response widened silently until that projection existed. The admin brand
	 * list does return it, which is how a picker knows not to offer a brand that
	 * can no longer take products.
	 */
	mergedIntoId?: string | null;
}

export interface CatalogCategory {
	id: string;
	name: string;
	slug: string;
	description?: string;
	parentId?: string;
	displayOrder: number;
	isActive: boolean;
}

/**
 * What `GET /catalog/categories?tree=true` returns: the same rows, nested.
 *
 * A separate type rather than an optional `children?` on `CatalogCategory`,
 * because the two responses are genuinely different shapes. The optional field
 * it replaced claimed every category might carry children, so consumers
 * recursed on a property the flat response has never once set -- which is how
 * the browse page came to render all 674 categories as one un-indented list.
 */
export interface CatalogCategoryTreeNode extends CatalogCategory {
	children: CatalogCategoryTreeNode[];
}

/**
 * One attribute of a category's spec template.
 *
 * This is the definition the product editor's Specifications tab renders a form
 * FROM. Nothing may hard-code an attribute name against it: the whole point of a
 * database-driven template is that a new category needs no code change, and plan
 * 008 V2 greps for hard-coded attribute names to keep it that way.
 */
export interface SpecTemplateAttribute {
	id: string;
	name: string;
	attributeType: string;
	isRequired: boolean;
	displayOrder: number;
	// Nullable, not merely optional: the columns are nullable and the API sends
	// `null` rather than omitting them. Declaring them optional-only made the
	// admin app fork this interface locally to add the nulls, which is exactly
	// what moving these types here was meant to prevent.
	unit?: string | null;
	enumValues?: string[] | null;
}

export interface SpecTemplateVariantAttribute {
	name: string;
	type: string;
	enumValues?: string[];
}

export interface SpecTemplate {
	id: string;
	categoryId: string;
	variantAttributes: SpecTemplateVariantAttribute[];
	attributes: SpecTemplateAttribute[];
}

/**
 * A specification value.
 *
 * `boolean` is included deliberately. The database column, the Zod schema and
 * the service have always accepted it; only the frontend type did not, which
 * made every boolean spec unrepresentable to consumers that trusted the type.
 */
export type SpecificationValue = string | number | boolean;

export type ProductSpecifications = Record<string, SpecificationValue>;

/**
 * The four things a catalogue image can be, from plan 008 section 3.
 *
 * A tuple rather than a bare union so the values have ONE OWNER: this is it.
 *
 * `schema.ts` imports `CatalogProductImage` from here for its row type. The
 * backend's Zod schema keeps a second RUNTIME tuple of the same four values,
 * not because the type is unreachable but because vitest's `@shared/types`
 * alias resolves type-only imports and not runtime values -- a constraint
 * `boq-templates.service.ts` records for its own value-shaped sets. That copy
 * is held to this one by a `satisfies` clause plus a compile-time assertion, so
 * adding a fifth value here fails the backend build until the copy follows.
 */
export const CATALOG_IMAGE_TYPES = [
	"front",
	"detail",
	"in_situ",
	"spec_sheet",
] as const;

export type CatalogImageType = (typeof CATALOG_IMAGE_TYPES)[number];

/**
 * One image, as the single-product detail response carries it.
 *
 * `alt` and `type` are nullable, and null means NOBODY HAS SAID. An empty
 * string or a guessed type reads the same as something a person decided, and
 * telling those apart is why the column stopped being a list of URLs.
 *
 * Primary is still POSITIONAL: the first entry is the primary image, exactly as
 * it was. No flag was added.
 *
 * ONLY THE DETAIL RESPONSE carries this shape. The list, search and
 * designer-facing projections still send `string[]`, because changing those
 * would turn a schema change into an API change across two other apps -- and
 * none of them renders anything an alt text would improve.
 */
/**
 * How long alt text may be.
 *
 * Long enough to describe a picture, short enough that a screen reader is not
 * read a paragraph before reaching the next control. Declared here because both
 * ends enforce it: the admin input caps typing at this length, and the server's
 * Zod schema is what actually decides.
 */
export const ALT_TEXT_MAX_LENGTH = 160;

/**
 * The cap as a TYPE, so the backend can be held to it.
 *
 * The backend cannot value-import from this package at runtime -- vitest's
 * `@shared/types` alias resolves types only -- so it keeps its own literal and
 * pins it against this. Same arrangement as `CATALOG_IMAGE_TYPES`.
 */
export type AltTextMaxLength = typeof ALT_TEXT_MAX_LENGTH;

export interface CatalogProductImage {
	url: string;
	type: CatalogImageType | null;
	alt: string | null;
}

export interface ProductVariant {
	id: string;
	productId: string;
	sku?: string;
	variantAttributes: Record<string, string | number>;
	/*
	 * Objects, not URLs, and the plan says otherwise -- its U2 leaves this a
	 * `string[]` and carves out only the product detail shape below.
	 *
	 * That cannot hold: a variant reaches a client only nested inside the detail
	 * response, and U4 has to edit a variant's alt text through the same media
	 * manager as a product's. Leaving this a URL list gives that text nowhere to
	 * travel, which is the exact argument KTD4 uses for the product carve-out.
	 * R6 and the variant half of plan 008's M5 are unbuildable without it.
	 */
	images: CatalogProductImage[];
	isActive: boolean;
}

/**
 * A price as the pricing projection serves it.
 *
 * `amount` is GST-INCLUSIVE and in paise, normalised from whatever basis the
 * source published. It is not `mrp`: two products quoted on different bases are
 * directly comparable here and are NOT comparable on the raw figure. That gap is
 * about 18%, and closing it is the whole reason this shape exists.
 */
export interface ProductPrice {
	amount: number;
	uom: string;
	basis: string;
	/** Ready to print beside the number, e.g. "MRP incl. GST". */
	basisLabel: string;
	gstRateBp: number;
	/**
	 * When the source published this. A CALENDAR DATE -- render it with
	 * `formatCalendarDate`, never `toLocaleDateString`, or it moves back a day
	 * for every reader west of UTC (#256).
	 */
	observedAt: string;
	staleAfter: string;
	/** Past its category's refresh cadence. Old, not wrong -- but say so. */
	isStale: boolean;
	confidence: number;
	observationId: string;
	variantId: string;
	city: string;
}

/**
 * Every tier a product may sit in.
 *
 * The array and the union together, for the same reason `PRODUCT_UOMS` exists:
 * a UI needs something to iterate, and a caller needs something to type against.
 * The admin editor previously kept its own `const TIERS = [...]` literal beside
 * this union -- two lists of the same three strings, which is how the third one
 * gets added to only one of them.
 */
export const PRODUCT_TIERS = ["standard", "premium", "luxury"] as const;

export type ProductTier = (typeof PRODUCT_TIERS)[number];
export type ProductStatus = "draft" | "active" | "inactive";

export const PRODUCT_ITEM_KINDS = [
	"material",
	"labour",
	"service",
	"composite",
] as const;
export type ProductItemKind = (typeof PRODUCT_ITEM_KINDS)[number];

export const PRODUCT_ITEM_KIND_ALL = "all" as const;
export type ProductItemKindFilter =
	| ProductItemKind
	| typeof PRODUCT_ITEM_KIND_ALL;

export const PRODUCT_ITEM_KIND_LABELS: Record<ProductItemKind, string> = {
	material: "Materials",
	labour: "Labour",
	service: "Services",
	composite: "Composite",
};

export interface CatalogProduct {
	id: string;
	brandId?: string;
	categoryId: string;
	name: string;
	description?: string;
	sku?: string;
	tier: ProductTier;
	status: ProductStatus;
	uom: string;
	/**
	 * The projected price to lead with. `null`/absent means this product has no
	 * price we are willing to state -- a first-class state to render, never `₹0`
	 * and never a fallback to `mrp`.
	 */
	price?: ProductPrice | null;
	/** Every projected row, for a range or a per-variant breakdown. */
	prices?: ProductPrice[];
	/**
	 * Whether this product can still be chosen -- shortlisted, or put in a BOQ.
	 *
	 * A sibling of `product` in the detail response, like `price` and `prices`,
	 * not a column on the products row. `false` means the product still exists
	 * and still renders (a BOQ line or quote pointing at it has to keep working),
	 * but it is off sale: its brand was withdrawn, it was discontinued, or it is
	 * not published. Absent on list payloads, which only ever carry sourceable
	 * products.
	 */
	sourceable?: boolean;
	/**
	 * Why it cannot be chosen, phrased for a customer ("its brand has been
	 * withdrawn from sale"). `null`/absent when it can.
	 */
	unsourceableReason?: string | null;
	/** The detail-response carve-out, per KTD4. */
	images: CatalogProductImage[];
	specifications: ProductSpecifications;
	brand?: CatalogBrand;
	category?: CatalogCategory;
	variants?: ProductVariant[];
	specTemplate?: SpecTemplate;
	createdAt: string;
}

/**
 * Why a product cannot be published yet.
 *
 * A publish gate that answers "validation failed" tells an operator to go
 * hunting; plan 008 V3 requires the response to NAME each missing thing, and
 * this is that shape. `field` is what the editor highlights, `message` is what
 * it prints, and `attributeName` carries the spec-template attribute when the
 * blocker is a missing specification rather than a column.
 */
export interface PublishBlocker {
	field: string;
	message: string;
	attributeName?: string;
}

/**
 * Every unit a catalogue product may be measured in.
 *
 * Mirrors the `uom` Postgres enum and the `uomValues` zod enum. It lives here
 * because a UI that lets an operator TYPE a unit is a UI that produces 400s --
 * the API accepts this closed set and nothing else.
 */
export const PRODUCT_UOMS = [
	"sq_ft",
	"liter",
	"piece",
	"set",
	"running_ft",
	"kg",
	"meter",
	"bag",
	"box",
	"roll",
	"bundle",
	"sq_m",
	"cft",
	"ton",
	"quintal",
	"pair",
	"dozen",
	"nos",
	"lot",
	"job",
] as const;

export type ProductUom = (typeof PRODUCT_UOMS)[number];

/**
 * One entry in a product's audit history, as the Provenance tab renders it.
 *
 * `changes` is empty for creates and deletes -- those carry the whole row under
 * `metadata.after` / `metadata.before` instead, because there is no diff to take.
 * The tab shows a field list when `changes` has entries and the action name
 * alone when it does not.
 */
export interface ProductHistoryEntry {
	id: string;
	/** `catalog.product.updated`, `catalog.variant.deleted`, and so on. */
	action: string;
	/** ISO 8601. */
	at: string;
	actor: {
		/**
		 * Null when the actor's user row is gone: `admin_action_logs.actor_user_id`
		 * is `on delete set null`, which is why the phone snapshot is taken at
		 * write time. The trail outlives the account.
		 */
		id: string | null;
		/** Email where the account has one, the write-time phone snapshot otherwise. */
		label: string;
	};
	changes: FieldChangeEntry[];
	/**
	 * One legible fact for entries that carry no diff -- a deleted variant's SKU,
	 * an uploaded image's filename. Null when the action needs none.
	 *
	 * Deliberately not the raw `metadata` column: that holds whole row snapshots
	 * for creates and deletes, and shipping it to a browser that renders none of
	 * it widened the audit table's exposure for no benefit.
	 */
	detail: string | null;
}

/**
 * One page of a product's audit history, including cursor for keyset pagination.
 */
export interface ProductHistoryPage {
	history: ProductHistoryEntry[];
	nextCursor?: string | null;
}

/** One field's movement within a history entry. */
export interface FieldChangeEntry {
	field: string;
	from: unknown;
	to: unknown;
}

/**
 * The catalogue dashboard's payload (plan 008 surface 6, acceptance V14).
 *
 * Declared here rather than inferred from the service, because the screen's
 * whole promise is that its numbers match the database and a client that
 * re-derived this shape could drift from the endpoint that produces it.
 */
export interface CatalogDashboard {
	products: {
		byStatus: { draft: number; active: number; inactive: number };
		/** Keyed by `verification_status`; absent keys mean zero rows. */
		byVerification: Record<string, number>;
		total: number;
	};
	blockers: {
		/** Draft products examined. Equal to `draftTotal` unless `capped`. */
		scanned: number;
		draftTotal: number;
		/** Of those scanned, how many carry at least one blocker. */
		blocked: number;
		/**
		 * Products stopped by each blocker, counted once per product per field.
		 *
		 * A product missing an image AND an HSN appears in both, so the sum is
		 * >= `blocked`. It is equal when every blocked product is stopped by
		 * exactly ONE OF THE THREE CATEGORIES -- not "missing exactly one thing",
		 * which the previous rewrite said: a draft missing three required
		 * attributes and nothing else counts once, because the specifications
		 * bucket counts products rather than attributes.
		 *
		 * The version before that said they "do NOT sum to `blocked`"
		 * unconditionally, and a screen repeated it to operators. This is the
		 * third attempt at one sentence about three numbers.
		 */
		byField: { images: number; hsnCode: number; specifications: number };
		/** The scan hit its cap, so every count above is a lower bound. */
		capped: boolean;
	};
	stalePrices: {
		count: number;
		/** ISO timestamp of the oldest lapsed price, or null when none is. */
		oldest: string | null;
		total: number;
	};
	recentActivity: CatalogActivityEntry[];
}

/** One row of the dashboard's recent-activity feed. */
export interface CatalogActivityEntry {
	id: string;
	/** A `catalog.<entity>.<verb>` action name. */
	action: string;
	at: string;
	subjectType: string;
	subjectId: string;
	/**
	 * An email, or `Admin <first 8 of the id>` when the account has none, or
	 * `Unknown admin` when there is no id either. Never the phone snapshot:
	 * `admin:catalog:read` carries no `admin:users:read`, so a holder could not
	 * look an admin up at all and must not be handed their number.
	 *
	 * Produced by the shared `actorLabel` in catalog.service.ts. The dashboard
	 * briefly carried its own copy that had drifted to lowercase; this comment
	 * documented the drifted form after the copy was deleted.
	 */
	actor: string;
}
